<template>
  <VWidget :enable-footer-actions="!item">
    <template slot="title">{{ title }}</template>

    <template slot="header-actions">
      <v-menu bottom left min-width="150" v-if="item">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon small class="mr-2">
            <v-icon small v-text="$vuetify.icons.values.options" />
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="destroy">
            <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn icon small @click="dismiss">
        <v-icon small>{{ $vuetify.icons.values.close }}</v-icon>
      </v-btn>
    </template>

    <template slot="content">
      <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
      <v-form>
        <v-text-field
          v-model.trim="form.display"
          :label="$t('displayName')"
          :error-messages="displayErrors"
          required
          @input="$v.form.display.$touch()"
          @blur="$v.form.display.$touch()"
        >
          <template slot="append" v-if="displayHasChanged">
            <v-btn depressed @click="doSubmit" :loading="loading" :disabled="loading">{{ $t('save') }}</v-btn>
          </template>
        </v-text-field>
      </v-form>

      <h3 class="text-h6 my-2" v-if="item">{{ $t('labels.transitions') }}</h3>

      <v-tabs v-model="activeTab" slider-color="grey darken-1" v-if="item">
        <v-tab ripple>
          <v-icon small class="mr-2">$vuetify.icons.values.ingress</v-icon>
          {{ $t('labels.ingress') }}
        </v-tab>
        <v-tab-item>
          <transitions-list
            :flow-id="flowId"
            :origin="form.name"
            :display="form.display"
            ingress
            :key="`list-from-${form.name}`"
          />
        </v-tab-item>
        <v-tab ripple>
          <v-icon small class="mr-2">$vuetify.icons.values.egress</v-icon>
          {{ $t('labels.egress') }}
        </v-tab>
        <v-tab-item>
          <transitions-list
            :flow-id="flowId"
            :origin="form.name"
            :display="form.display"
            :key="`list-to-${form.name}`"
          />
        </v-tab-item>
      </v-tabs>
    </template>

    <template slot="footer-actions" v-if="!item">
      <v-btn depressed color="accent" @click="doSubmit" :loading="loading" :disabled="loading">{{
        $t('create')
      }}</v-btn>
    </template>
  </VWidget>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { NAMESPACE } from '../store'
import { BaseForm, FormErrors, VWidget } from '@argon/app/components'
import TransitionsList from './TransitionsList'
import { required } from 'vuelidate/lib/validators'
import ConfirmDeleteStateDialog from './ConfirmDeleteStateDialog'
const confirmDeleteState = create(ConfirmDeleteStateDialog)

export default {
  name: 'FlowStateCard',
  extends: BaseForm,
  components: {
    VWidget,
    FormErrors,
    TransitionsList
  },
  validations: {
    form: {
      display: { required }
    }
  },
  props: {
    item: Object,
    flowId: {
      type: [String, Number],
      required: true
    },
    dismiss: { type: Function, required: true }
  },
  data: () => ({
    form: {
      display: ''
    },
    activeTab: null,
    loading: false
  }),
  computed: {
    ...mapState(NAMESPACE, ['current']),
    title() {
      return this.item ? this.$t('editTitle') : this.$t('newTitle')
    },
    displayHasChanged() {
      return this.item && this.form.display !== this.item.display
    },
    displayErrors() {
      const errors = []
      if (!this.$v.form.display.$dirty) return errors
      !this.$v.form.display.required && errors.push(this.$t('required'))
      this.getServerErrors('label', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['createState', 'updateState', 'deleteState']),
    doSubmit() {
      this.resetServerErrors()
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      this.loading = true
      let callback = this.item ? this.updateState : this.createState
      let payload = { flowId: this.flowId, data: this.form }
      callback(payload)
        .then((created) => {
          if (!this.item) {
            this.form = { ...created }
            let params = { ...this.$route.params, stateName: created.name }
            this.$router.push({ name: 'flowState', params })
          }
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.loading = false
        })
    },
    destroy() {
      return confirmDeleteState().then((res) => {
        if (res) {
          this.resetServerErrors()
          let payload = { flowId: this.flowId, data: this.form }
          return this.deleteState(payload)
            .then(() => {
              this.dismiss()
            })
            .catch(this.setServerErrors)
        }
      })
    }
  },
  created() {
    if (this.item) {
      this.form = { ...this.item }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .v-tabs {
  .v-tabs__bar {
    border-bottom-style: solid;
    border-bottom-color: var(--v-grey-base);
    border-bottom-width: 1px;
  }

  .v-tabs__item--active {
    font-size: bolder !important;
  }
}
</style>

<i18n>
{
  "en": {
    "create": "@:labels.create",
    "delete": "@:labels.delete",
    "displayName": "@:labels.displayName",
    "editTitle": "State",
    "newTitle": "New State",
    "required": "@:labels.required",
    "save": "@:labels.save"
  },
  "fr": {
    "create": "@:labels.create",
    "delete": "@:labels.delete",
    "displayName": "@:labels.displayName",
    "editTitle": "État",
    "newTitle": "Nouvel état",
    "required": "@:labels.required",
    "save": "@:labels.save"
  }
}
</i18n>
